import React from 'react';
import PropTypes from 'prop-types';
import {FileUploadV2} from '@pepsico-ds/ui';
import {TextInput, LanguageTabs, ButtonWithIcon} from '@/components/atoms';
import UploadProgressBar from '@/components/atoms/UploadProgressBar';

const ValidationErrorMessage = ({error, testId}) => {
  if (!error) return null;

  return (
    <div
      className='mt-1 w-full text-sm text-support-error-primary'
      data-testid={testId}>
      {error}
    </div>
  );
};

const GameDisplaySection = ({
  languages,
  activeTabIndex,
  setActiveTabIndex,
  currentLangData,
  updateField,
  handleImageUpload,
  handleImageDelete,
  handleImageFileError,
  imageUploadProgress,
  imageValidationError,
  setShowGuidelines
}) => {
  // TODO: Restore Download Sample Image when sample download is implemented
  // const handleDownloadSample = () => {
  //   // Placeholder for sample image download
  // };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center gap-6'>
        {/* TODO: Restore Download Sample Image when sample download is implemented
        <ButtonWithIcon
          id='view-guideline'
          iconLeading='download'
          handleButton={() => handleDownloadSample}
          uiSchema={{
            title: 'Download Sample Image',
            variant: 'tertiary',
            size: 'small',
            isDark: true
          }}
        />
        */}
        <ButtonWithIcon
          id='view-guideline'
          iconLeading='open_in_full'
          handleButton={() => setShowGuidelines(true)}
          uiSchema={{
            title: 'View Guideline',
            variant: 'tertiary',
            size: 'small',
            isDark: true
          }}
        />
      </div>

      <div className='max-w-[380px]'>
        <div className='game-image-container w-fit rounded-lg border border-[color:var(--color-border-default)] p-3'>
          {imageUploadProgress > 0 && (
            <UploadProgressBar
              progress={imageUploadProgress}
              processingLabel='Processing image...'
            />
          )}
          <FileUploadV2
            accept='.jpg,.png,.webp'
            buttonLabel='Upload file'
            description='Drag and drop your file here (only .webp, .jpg, .png)'
            feedbackType={imageValidationError ? 'error' : 'info'}
            label={{
              label: 'Game Image*',
              showLabelIcon: true,
              infoIconTooltip: {
                text: 'Max 70 KB'
              }
            }}
            type='image'
            variant={currentLangData.gameImageUri ? 'edit' : 'default'}
            imageUri={currentLangData.gameImageUri || undefined}
            uploadIcon='image'
            onUpdate={(files) => handleImageUpload(files)}
            onDelete={() => handleImageDelete()}
            onFileError={(error) => handleImageFileError?.(error)}
            data-testid='game-image-upload'
          />
        </div>
        <ValidationErrorMessage
          error={imageValidationError}
          testId='game-image-validation-error'
        />
      </div>

      <LanguageTabs
        languages={languages}
        activeTabIndex={activeTabIndex}
        setActiveTabIndex={(index) => setActiveTabIndex(index)}
        testIdPrefix='name-language-tab'
        dividerClassName='-mt-4 mb-0 border-0 border-b'
      />
      <div className='flex gap-4'>
        <div className='max-w-[380px] flex-1'>
          <TextInput
            value={currentLangData.name}
            onChange={(val) => updateField('name', val)}
            label='Title*'
            isDark
            maxLength={55}
            placeholderText='Title'
            id='display-title'
            showLabelIcon
            infoIconTooltip='Max 2 lines (50–55 characters). Extra text will be truncated.'
          />
        </div>
        <div className='max-w-[380px] flex-1'>
          <TextInput
            value={currentLangData.tagDescription}
            onChange={(val) => updateField('tagDescription', val)}
            label='Tag Description*'
            isDark
            maxLength={85}
            placeholderText='Tag Description'
            id='display-tag-description'
            showLabelIcon
            infoIconTooltip='Max 2 lines (80–85 characters). Extra text will be truncated.'
          />
        </div>
      </div>
    </div>
  );
};

GameDisplaySection.propTypes = {
  languages: PropTypes.array.isRequired,
  activeTabIndex: PropTypes.number.isRequired,
  setActiveTabIndex: PropTypes.func.isRequired,
  currentLangData: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  handleImageUpload: PropTypes.func.isRequired,
  handleImageDelete: PropTypes.func.isRequired,
  handleImageFileError: PropTypes.func,
  imageUploadProgress: PropTypes.number,
  imageValidationError: PropTypes.string,
  setShowGuidelines: PropTypes.func.isRequired
};

ValidationErrorMessage.propTypes = {
  error: PropTypes.string,
  testId: PropTypes.string.isRequired
};

export default React.memo(GameDisplaySection);
